import { isLegacyLevelDatum } from "building-types/datums/datumEntityTypes";
import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type {
  DocEntityHandle,
  GroupableEntityHandle,
  GroupHandle,
  RoomHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { Table } from "common/data/table";
import { assert } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import lruMemo from "common/utils/lruMemo";
import { docGetCatalogItemParentCatalog } from "../docGetCatalogItem";
import { docGetElectricalRuleParent } from "../docGetElectricalRules";
import { docGetEdgeModifier } from "../docGetModifiers";
import { roomHasValidBlock } from "../elements/docGetBlocks";
import { getFloorIdForBay, getFloorIdForSplitLine } from "../elements/floor/floorAssembly";
import { isRoomLayoutEligibleDocEntityHandle } from "../rooms/layout/roomLayoutEligibleEntityTypes";
import { roomLayoutEligibleEntityTypes } from "../rooms/layout/roomLayoutEligibleEntityTypes";
import { docEntityHandleHash } from "./core/handleHash";
import { getDocEntityParentTypes } from "./docEntityConstraintTools";
import { docGetEntityHandleByHash } from "./docEntityHandleHash";
import type { DocEntityTypeToParentHandleType } from "./docEntityTypeToParentHandleType";
import { docGetEntityHandleById } from "./docGetEntityHandleById";
import { docGetTableEntity } from "./docGetTableEntity";

/**
 * docEntityGetParent is used to determine the parent of a given entity handle.
 *
 * Importantly, this function is general purpose and not simply used to present the UI,
 * so you should avoid the temptation to modify it to fix a specific UI bug. If you need
 * special presentation behavior, consider using a separate function.
 *
 */
export default function docEntityGetParent<T extends { type: DocEntityType }>(
  doc: Doc,
  handle: DocEntityHandle & T,
): DocEntityTypeToParentHandleType[T["type"]] | undefined {
  const getParentHandle = (parentHandle: DocEntityHandle | undefined) => {
    if (!parentHandle) {
      return;
    }

    const parentTypes = getDocEntityParentTypes(handle.type);

    if (!parentTypes.length) {
      return parentHandle as DocEntityTypeToParentHandleType[T["type"]];
    }

    assert(parentTypes.includes(parentHandle.type as any), `Invalid parent type for ${handle.type}`);
    return parentHandle as DocEntityTypeToParentHandleType[T["type"]];
  };

  if (handle.type in roomLayoutEligibleEntityTypes) {
    const roomLayoutId = docEntityRoomLayoutId(doc, handle);

    if (roomLayoutId) {
      return getParentHandle({ type: "ROOM_LAYOUT", id: roomLayoutId });
    }
  }

  switch (handle.type) {
    case "DOC_REF":
    case "BUILDER_REF":
    case "OPTION_SET_REF":
    case "OPTION_RULE":
    case "LEVEL":
    case "PALETTE":
    case "REFERENCE_GEOM":
    case "ELEVATION":
    case "MODIFIER":
    case "UNDERLAY":
    case "ASSET":
    case "SETTINGS_SET":
    case "ENTITY_SETTINGS":
    case "OPTION_TEMPLATE":
    case "STAIR_SYSTEM":
    case "RAILING":
    case "FACADE":
    case "SHEET":
    case "EDIT_MODE":
    case "GENERATED_ENTITY":
    case "AUTO_DIMENSION":
    case "FOUNDATION_SYSTEM":
    case "ROOF_SYSTEM":
    case "ROOF_VOLUME":
    case "VIEW_3D":
    case "PLAN_VIEW":
    case "ELEVATION_VIEW":
    case "SITE":
    case "PLAN_SET_TEMPLATE":
    case "CONTENT_TEMPLATE":
    case "CONTENT_REPLICATOR":
    case "PRESET":
    case "VIEW_TEMPLATE":
    case "SECTION_VIEW":
    case "DATA_SEQUENCE":
    case "LABEL":
    case "REPORTABLE_UNIT_DESIGNATION":
    case "VIEW_TEMPLATE_OVERRIDE":
    case "RULE_BASED_SELECTOR":
    case "GLOBAL_PARAMETER":
    case "SYSTEM_PARAMETER":
    case "INDETERMINATE_ENTITY_TYPE":
    case "FRAMING_COMPONENT":
    case "FOUNDATION_ASSEMBLY":
    case "FOUNDATION_COMPONENT":
    case "HEADER_ASSEMBLY":
    case "COMPONENT":
    case "COMPONENT_INSTANCE":
    case "FRAME_FLOOR_ASSEMBLY":
    case "SLAB_FLOOR_ASSEMBLY":
    case "SLAB_COMPONENT":
    case "SHEATHING_COMPONENT":
    case "MASONRY_COMPONENT":
    case "OPTION_SET":
    case "INSTANCE_OPTION_SET":
    case "OPTION_LOCK":
    case "FLOOR_SYSTEM":
    case "COMPOSITE_LEVEL":
      break;
    case "FLOOR_BAY": {
      const bayFloorId = getFloorIdForBay(doc.floors, handle.id);
      return bayFloorId ? getParentHandle({ type: "FLOOR", id: bayFloorId }) : undefined;
    }
    case "FLOOR_SPLIT_LINE": {
      const slFloorId = getFloorIdForSplitLine(doc.floors, handle.id);
      return slFloorId ? getParentHandle({ type: "FLOOR", id: slFloorId }) : undefined;
    }
    case "GROUP": {
      const groupParent = docGetGroupParent(doc, handle);

      if (!groupParent) {
        break;
      }

      return getParentHandle(groupParent);
    }
    case "OPTION_EFFECT": {
      const effect = docGetTableEntity(doc, handle);

      if (!effect?.parentId) {
        break;
      }

      return getParentHandle({ type: "OPTION_RULE", id: effect.parentId });
    }
    case "FACADE_SWEEP_STRATUM":
    case "FACADE_SPAN_STRATUM": {
      const stratum = docGetTableEntity(doc, handle);

      if (!stratum) {
        return undefined;
      }

      if (stratum.parentId && doc.facades[stratum.parentId]) {
        return getParentHandle({ type: "FACADE", id: stratum.parentId });
      }

      return undefined;
    }
    case "DRAWING_REF": {
      const { parentId } = doc.drawingRefs[handle.id];

      if (!parentId) {
        return undefined;
      }

      // attempt to find handle in either doc.roomLayouts or doc.sheets
      const room = doc.rooms[parentId];
      const sheetHandle = doc.sheets[parentId];

      if (room) {
        return getParentHandle({
          id: parentId,
          type: "ROOM",
        });
      } else if (sheetHandle) {
        return getParentHandle({
          id: parentId,
          type: "SHEET",
        });
      }
      return undefined;
    }
    case "STAIR_BLOCK": {
      const block = docGetTableEntity(doc, handle);

      if (!block) {
        return undefined;
      }

      return getParentHandle({ id: block.stairSystemId, type: "STAIR_SYSTEM" });
    }
    case "RAILING_POST": {
      const post = docGetTableEntity(doc, handle);

      if (!post?.parentId) {
        return undefined;
      }

      return getParentHandle({ id: post.parentId, type: "RAILING" });
    }
    case "COLUMN": {
      const column = docGetTableEntity(doc, handle);

      if (!column?.parentId) {
        return undefined;
      }

      const roomHandle: RoomHandle = { type: "ROOM", id: column.parentId };
      const room = docGetTableEntity(doc, roomHandle);

      if (!room) {
        return undefined;
      }

      return getParentHandle(roomHandle);
    }
    case "BLOCK": {
      const block = docGetTableEntity(doc, handle);

      if (!block?.levelId) {
        return undefined;
      }

      return getParentHandle({ id: block.levelId, type: "LEVEL" });
    }
    case "ROOM": {
      const room = docGetTableEntity(doc, handle);

      if (!room?.parentId) {
        return undefined;
      }

      if (!roomHasValidBlock(doc, room)) {
        return undefined;
      }

      return getParentHandle({ type: "BLOCK", id: room.parentId });
    }
    case "ROOM_LAYOUT": {
      const roomLayout = docGetTableEntity(doc, handle);

      if (!roomLayout?.parentId) {
        return undefined;
      }

      return getParentHandle({ type: "ROOM", id: roomLayout.parentId });
    }
    case "ROOM_LAYOUT_INSTANCE": {
      const roomLayoutInstance = docGetTableEntity(doc, handle);

      if (!roomLayoutInstance?.parentId) {
        return undefined;
      }

      return getParentHandle({ type: "ROOM", id: roomLayoutInstance.parentId });
    }
    case "OPENING": {
      const opening = docGetTableEntity(doc, handle);

      if (!opening?.parentId) {
        return undefined;
      }

      const roomHandle: RoomHandle = { type: "ROOM", id: opening.parentId };
      const room = docGetTableEntity(doc, roomHandle);

      if (!room) {
        return undefined;
      }

      return getParentHandle(roomHandle);
    }
    case "ANNOTATION": {
      const annot = docGetTableEntity(doc, handle);

      if (!annot?.parentId) {
        return undefined;
      }

      const annotationParentTypes = getDocEntityParentTypes(handle.type);
      const parentHandle = docGetEntityHandleById(doc, annot.parentId, annotationParentTypes);

      if (!parentHandle) {
        break;
      }

      return getParentHandle(parentHandle);
    }
    case "PROP": {
      if (handle.settingsHandle?.id) {
        return getParentHandle(handle.settingsHandle);
      }
      return undefined;
    }
    case "CASEWORK":
    case "PROP_INSTANCE":
    case "CEILING": {
      const entity = docGetTableEntity(doc, handle);

      if (!entity?.parentId) {
        return undefined;
      }

      return getParentHandle({ type: "ROOM", id: entity.parentId });
    }
    case "FLOOR": {
      const entity = docGetTableEntity(doc, handle);
      if (entity && entity.parentId) {
        const parentEntity = docGetTableEntity(doc, { type: "ROOM", id: entity.parentId });
        if (parentEntity) {
          return { type: "ROOM", id: entity.parentId } as DocEntityTypeToParentHandleType[T["type"]];
        }
        return { type: "FLOOR_SYSTEM", id: entity.parentId } as DocEntityTypeToParentHandleType[T["type"]];
      }
      return undefined;
    }
    case "FLOOR_FINISH": {
      const floorFinish = docGetTableEntity(doc, handle);

      if (!floorFinish?.parentId) {
        return undefined;
      }

      return getParentHandle({ type: "ROOM", id: floorFinish.parentId });
    }
    case "EDGE_BEHAVIOR": {
      const edgeBehavior = docGetTableEntity(doc, handle);

      if (!edgeBehavior) {
        return undefined;
      }

      switch (edgeBehavior.edgeBehaviorType) {
        case "FOUNDATION_PATTERN": {
          const room = docGetTableEntity(doc, { type: "ROOM", id: edgeBehavior.parentId });

          if (room) {
            return getParentHandle({ type: "ROOM", id: edgeBehavior.parentId });
          }

          const stairBlock = docGetTableEntity(doc, { type: "STAIR_BLOCK", id: edgeBehavior.parentId });

          if (stairBlock) {
            return getParentHandle({ type: "STAIR_BLOCK", id: edgeBehavior.parentId });
          }

          return undefined;
        }
        default:
          return getParentHandle({ type: "ROOM", id: edgeBehavior.parentId });
      }
    }
    case "ROOF": {
      const roof = docGetTableEntity(doc, handle);

      if (!roof?.parentId) {
        return undefined;
      }

      return getParentHandle({ type: "ROOF_SYSTEM", id: roof.parentId });
    }
    case "FOUNDATION": {
      const foundation = docGetTableEntity(doc, handle);

      if (!foundation?.parentId) {
        return undefined;
      }

      return getParentHandle({ type: "FOUNDATION_SYSTEM", id: foundation.parentId });
    }
    case "SHAPE":
      return getParentHandle(handle.parent);

    case "EDGE":
    case "VERTEX":
      return getParentHandle({ type: "SHAPE", parent: handle.parent, shapeId: handle.shapeId });
    case "OPTION_SET_OVERRIDES": {
      const optionSetOverrides = docGetTableEntity(doc, handle);

      if (!optionSetOverrides?.parentId) {
        return undefined;
      }

      return getParentHandle({ type: "OPTION_TEMPLATE", id: optionSetOverrides.parentId });
    }
    case "OPTION":
      return getParentHandle({ type: "OPTION_SET", id: handle.optionSetId });

    case "ELECTRICAL_DEVICE": {
      const device = docGetTableEntity(doc, handle);

      if (!device?.parentId) {
        return undefined;
      }

      return getParentHandle({ type: "ROOM", id: device.parentId });
    }
    case "ELECTRICAL_LAYOUT": {
      const layout = docGetTableEntity(doc, handle);

      if (!layout?.parentId) {
        return undefined;
      }

      return getParentHandle({ type: "ROOM", id: layout.parentId });
    }
    case "ELECTRICAL_RULE":
      return getParentHandle(docGetElectricalRuleParent(handle));
    case "SETTINGS": {
      const settings = docGetTableEntity(doc, handle);

      if (!settings?.parentId) {
        return undefined;
      }

      const { parentId } = settings;

      if (doc.settingsSets[parentId]) {
        return getParentHandle({ type: "SETTINGS_SET", id: parentId });
      }

      if (doc.rooms[parentId]) {
        return getParentHandle({ type: "ROOM", id: parentId });
      }

      if (doc.levels[parentId]) {
        return getParentHandle({ type: "LEVEL", id: parentId });
      }

      return undefined;
    }
    case "DORMER":
    case "SKYLIGHT": {
      const dormer = docGetTableEntity(doc, handle);

      if (!dormer?.parentId) {
        return undefined;
      }

      return getParentHandle({ type: "ROOF", id: dormer.parentId });
    }
    case "ROOF_OPENING": {
      const roofOpening = docGetTableEntity(doc, handle);

      if (!roofOpening?.parentId) {
        return undefined;
      }
      return getParentHandle({ type: "ROOF", id: roofOpening.parentId });
    }
    case "EDGE_MODIFIER": {
      const edgeModifier = docGetEdgeModifier(doc, handle);

      if (!edgeModifier) {
        return undefined;
      }

      const { parentId } = edgeModifier;

      if (doc.rooms[parentId]) {
        return getParentHandle({ type: "ROOM", id: parentId });
      }

      if (doc.roofs[parentId]) {
        return getParentHandle({ type: "ROOF", id: parentId });
      }

      if (doc.railings[parentId]) {
        return getParentHandle({ type: "RAILING", id: parentId });
      }

      return undefined;
    }
    case "MEASUREMENT": {
      const measurement = docGetTableEntity(doc, handle);

      if (!measurement) {
        return undefined;
      }

      const { parentId } = measurement;

      if (doc.rooms[parentId]) {
        return getParentHandle({ type: "ROOM", id: parentId });
      }

      return undefined;
    }
    case "SITE_SHAPE": {
      const shape = docGetTableEntity(doc, handle);

      if (!shape?.parentId) {
        return undefined;
      }

      return getParentHandle({ type: "SITE", id: shape.parentId });
    }
    case "ROOF_SIDE":
      return getParentHandle(handle.parent);
    case "FLOOR_PERIMETER_OPENING":
      return getParentHandle({ type: "FLOOR", id: handle.floorId });

    case "CATALOG_ITEM": {
      const parentCatalog = docGetCatalogItemParentCatalog(doc, handle);

      if (!parentCatalog) {
        return undefined;
      }

      return getParentHandle(parentCatalog);
    }
    case "CONTENT_TEMPLATE_INSTANCE":
    case "LOCAL_PARAMETER":
    case "PLAN_SET_TEMPLATE_CONFIG":
    case "DATA_TABLE":
    case "WALL_PLANE_ASSEMBLY":
    case "WALL_PLANE_FILTER":
    case "WALL_PLANE_RULE":
    case "WALL_PLANE_TRIM":
    case "EFFECT":
    case "SELECTOR":
    case "VIEW_SELECTOR":
    case "VIEW_OVERRIDE":
    case "RULE_BASED_SELECTOR_INSTANCE":
    case "VIEWPORT": {
      /**
       * NOTE: Essentially all table-based entities could move to this style of parent entity lookup
       **/
      const entity = docGetTableEntity(doc, handle);

      if (!entity?.parentId) {
        return undefined;
      }

      const parentTypes = getDocEntityParentTypes(handle.type);

      const parentHandle = docGetEntityHandleById(doc, entity.parentId, parentTypes);

      if (!parentHandle) {
        return undefined;
      }

      return getParentHandle(parentHandle);
    }
    case "DATUM": {
      const datum = docGetTableEntity(doc, handle);

      if (!isLegacyLevelDatum(datum) || !datum.levelId) {
        return undefined;
      }

      return getParentHandle({ type: "LEVEL", id: datum.levelId });
    }
    case "PROFILE":
      return undefined;
    default:
      assertUnreachable(handle);
  }

  return undefined;
}

function docEntityRoomLayoutId(doc: Doc, handle: DocEntityHandle) {
  if (!isRoomLayoutEligibleDocEntityHandle(handle)) {
    return undefined;
  }

  const hash = docEntityHandleHash(handle);
  return doc.entityRoomLayouts[hash];
}

export function docGetGroupParent(doc: Doc, groupHandle: GroupHandle) {
  /**
   * The cast to unknown is provisional for the strict null retreat
   * and a better fix would be to modify DocEntityTypeToParentHandleType
   * to properly represent the kinds of handle types that can be a parent
   * to a group, but that triggers a cascade of changes that are not worth it.
   */
  return getGroupToParentMap({ doc })[groupHandle.id] as unknown as DocEntityTypeToParentHandleType["GROUP"];
}

const getGroupToParentMap = lruMemo(({ doc }: { doc: Doc }) => {
  const groupToParentMap: { [groupId: string]: DocEntityHandle } = {};

  for (const hash in doc.entityGroups) {
    const groupId = doc.entityGroups[hash];

    if (groupToParentMap[groupId]) {
      continue;
    }

    const childHandle = docGetEntityHandleByHash(doc, hash) as GroupableEntityHandle;
    if (!childHandle) {
      continue;
    }

    const parent = docEntityGetParent(doc, childHandle);
    if (parent) {
      groupToParentMap[groupId] = parent;
    }
  }

  return groupToParentMap as Table<DocEntityHandle>;
}, 4);
